<template>
  <div class="page-wrap" :class="{ night }">
    <!-- ① 顶部固定：导航 + 彩种 + 时间 tab -->
    <header class="record-top">
      <AppHeader />

      <button class="game-pick" type="button" @click="openSheet">
        {{ currentGame.name }}
        <i class="caret" />
      </button>

      <div class="date-tabs">
        <button
          v-for="tab in dateTabs"
          :key="tab.key"
          class="date-tab"
          :class="{ on: dateRange === tab.key }"
          type="button"
          @click="dateRange = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>
    </header>

    <!-- ② 中间可滚动：列表 / 空状态 / 加载（隐藏滚动条） -->
    <main class="record-scroll">
      <div v-if="loading" class="loading-tip">加载中...</div>

      <div v-else-if="!orders.length" class="empty-state">
        <img class="empty-state__icon" :src="emptyIcon" alt="" />
        <p class="empty-state__title">暂无记录</p>
        <p class="empty-state__hint">
          不要让大奖溜走，<button type="button" class="empty-state__link" @click="goBet">购彩去</button>
        </p>
      </div>

      <ul v-else class="record-list">
        <li v-for="order in orders" :key="order.id" class="record-item">
          <img class="record-logo" :src="gameIcon(order)" alt="" />
          <div class="record-body">
            <div class="record-head">
              <div class="record-game">{{ order.gameName }}</div>
              <span class="record-status" :class="statusClass(order.status)">{{ statusText(order.status) }}</span>
            </div>
            <div class="record-play">{{ playLabel(order) }}</div>
            <div class="record-content">{{ order.content || '—' }}</div>
            <div class="record-meta">
              <span class="record-numero">期号 {{ order.numero }}</span>
              <span class="record-time">{{ fmtTime(order.createdAt) }}</span>
            </div>
            <div class="record-foot">
              <span class="record-amount">投注 {{ fmtMoney(order.amount) }} U</span>
              <span v-if="order.status === 'win'" class="record-win">中奖 {{ fmtMoney(order.winAmount) }} U</span>
            </div>
          </div>
        </li>
      </ul>
    </main>

    <!-- ③ 底部固定：金额合计 -->
    <footer class="sum-foot">
      <div class="sum-row">
        <span class="sum-label">计划投注金额合计:</span>
        <span class="sum-value">{{ fmtSumMoney(summary.plannedBet) }} U</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">有效投注金额合计:</span>
        <span class="sum-value">{{ fmtSumMoney(summary.betAmount) }} U</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">中奖金额合计:</span>
        <span class="sum-value">{{ fmtSumMoney(summary.winAmount) }} U</span>
      </div>
    </footer>

    <div v-if="sheet" class="mask" @click="sheet = false">
      <div class="lot-panel" @click.stop>
        <div class="lot-title">彩种选择</div>
        <div class="lot-scroll">
          <button
            class="lot-game"
            :class="{ on: showAll }"
            type="button"
            @click="pickLottery(0)"
          >
            全部
          </button>
          <button
            v-for="game in pickerGames"
            :key="game.id"
            class="lot-game"
            :class="{ on: !showAll && gameId === game.id }"
            type="button"
            @click="pickLottery(game.id)"
          >
            {{ game.name }}
          </button>
        </div>
        <button class="lot-cancel" type="button" @click="sheet = false">取消</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppHeader from '@/components/AppHeader.vue'
import { games, visibleGames, syncGameVisibility } from '@/data/games'
import { lgwApi } from '@/api/lgw'
import emptyIcon from '@/assets/lobby/ui/empty-record.png'
import kenoIcon from '@/assets/lobby/icon/KENO.png'
import { useTheme } from '@/composables/useTheme'
import { useActiveGame } from '@/composables/useActiveGame'
import { getBetPath } from '@/composables/betRoute'
import { findSsqPlayLabel } from '@/data/ssq'

const TC_KENO8_ID = 21503

const dateTabs = [
  { key: 'today', label: '今天' },
  { key: 'yesterday', label: '昨天' },
  { key: 'week', label: '近七天' },
  { key: 'month', label: '近三十天' },
  { key: 'custom', label: '自定义' }
]

const CN_PICK = ['', '一', '二', '三', '四', '五', '六', '七']

const route = useRoute()
const router = useRouter()
const { night } = useTheme()
const { gameId, currentGame: activeGame, setActiveGame } = useActiveGame()

/** 后端 /games/mobile 拉平后的彩种列表；失败时回退 visibleGames */
const remoteGames = ref([])

const pickerGames = computed(() => {
  if (remoteGames.value.length) return remoteGames.value
  return visibleGames.value
})

const showAll = ref(Number(route.query.id) === 0)
if (!showAll.value && !route.query.id) {
  setActiveGame(TC_KENO8_ID)
  router.replace({ path: '/record', query: { id: TC_KENO8_ID } })
} else if (!showAll.value) {
  setActiveGame(Number(route.query.id))
}

const dateRange = ref('today')
const sheet = ref(false)
const loading = ref(false)
const orders = ref([])
const summary = ref({
  plannedBet: 0,
  betAmount: 0,
  winAmount: 0
})

const currentGame = computed(() => {
  if (showAll.value) return { id: 0, name: '全部' }
  const fromRemote = remoteGames.value.find((g) => Number(g.id) === Number(gameId.value))
  if (fromRemote) return fromRemote
  return activeGame.value
})

const queryParams = computed(() => {
  const q = { range: dateRange.value === 'custom' ? 'today' : dateRange.value }
  if (!showAll.value && gameId.value) q.gameId = gameId.value
  return q
})

const fmtMoney = (n) => Number(n || 0).toFixed(2)
/** 底部合计对齐静态页：四位小数 */
const fmtSumMoney = (n) => Number(n || 0).toFixed(2)

const fmtTime = (ts) => {
  const d = new Date(Number(ts) || Date.now())
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const statusText = (status) => {
  if (status === 'win') return '已中奖'
  if (status === 'lose') return '未中奖'
  if (status === 'cancel') return '已撤单'
  return '待开奖'
}

const statusClass = (status) => {
  if (status === 'win') return 'win'
  if (status === 'lose') return 'lose'
  if (status === 'cancel') return 'cancel'
  return 'pending'
}

const playLabel = (order) => {
  if (order.selection?.mode === 'fun') return '趣味'
  if (order.selection?.mode === 'pick') {
    const t = order.selection.pickType
    return `任选${CN_PICK[Number(t)] || t}`
  }
  const key = order.playKey || order.items?.[0]?.playKey || ''
  if (key === 'keno-fun') return '趣味'
  const m = /^keno-pick-(\d)$/.exec(key)
  if (m) return `任选${CN_PICK[Number(m[1])] || m[1]}`
  // 超级大乐透
  if (Number(order.gameId) === 16 || String(key).startsWith('dlt-')) {
    const base = order.playName || key || '超级大乐透'
    return order.append ? `${base}·追加` : base
  }
  // 双色球（无追加）
  if (Number(order.gameId) === 17 || String(key).startsWith('ssq-')) {
    return order.playName || findSsqPlayLabel(key) || key || '双色球'
  }
  if (order.playName) return order.playName
  if (order.count > 1) return `${order.count} 注`
  return '投注'
}

const gameIcon = (order) => {
  if (Number(order.gameId) === TC_KENO8_ID || order.group === 'KENO') return kenoIcon
  const g = games.value.find((item) => item.id === Number(order.gameId))
  return g?.icon || kenoIcon
}

/** 从网关拉取启用中的彩种，供「彩种选择」列表 */
const loadPickerGames = async () => {
  await syncGameVisibility()
  try {
    const res = await lgwApi.gamesMobile()
    if (!res?.success || !Array.isArray(res.data?.groups)) return
    const localById = new Map(games.value.map((g) => [Number(g.id), g]))
    const list = []
    const seen = new Set()
    for (const grp of res.data.groups) {
      for (const g of grp.games || []) {
        const id = Number(g.id ?? g.gameId)
        if (!id || seen.has(id) || g.enabled === false) continue
        seen.add(id)
        const local = localById.get(id)
        list.push({
          id,
          name: g.name || local?.name || String(id),
          group: g.group || g.groupCode || local?.group || '',
          icon: local?.icon || kenoIcon,
          enabled: true
        })
      }
    }
    if (list.length) remoteGames.value = list
  } catch (_) {
    /* 回退 visibleGames */
  }
}

const loadRecords = async () => {
  loading.value = true
  try {
    const [listRes, sumRes] = await Promise.all([
      lgwApi.orders({ ...queryParams.value, size: 50 }),
      lgwApi.orderSum(queryParams.value)
    ])
    if (listRes.success) {
      orders.value = (listRes.data?.list || []).slice().sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    } else {
      orders.value = []
    }
    if (sumRes.success && sumRes.data) {
      summary.value = {
        plannedBet: sumRes.data.plannedBet ?? sumRes.data.betAmount ?? 0,
        betAmount: sumRes.data.betAmount ?? 0,
        winAmount: sumRes.data.winAmount ?? 0
      }
    } else {
      summary.value = { plannedBet: 0, betAmount: 0, winAmount: 0 }
    }
  } catch {
    orders.value = []
    summary.value = { plannedBet: 0, betAmount: 0, winAmount: 0 }
  } finally {
    loading.value = false
  }
}

const openSheet = async () => {
  sheet.value = true
  await loadPickerGames()
}

const pickLottery = (id) => {
  sheet.value = false
  if (id === 0) {
    showAll.value = true
    router.replace({ path: '/record', query: { id: 0 } })
    return
  }
  showAll.value = false
  setActiveGame(id)
  router.replace({ path: '/record', query: { id } })
}

const goBet = () => {
  const id = showAll.value ? TC_KENO8_ID : gameId.value
  router.push({ path: getBetPath(id), query: { id } })
}

watch([dateRange, showAll, gameId], () => {
  loadRecords()
})

watch(
  () => route.fullPath,
  () => {
    if (route.path === '/record') loadRecords()
  }
)

onMounted(() => {
  loadPickerGames()
  loadRecords()
})
</script>

<style scoped>
/* ========== 布局：顶底固定 + 中间滚动 ========== */
.page-wrap {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 480px;
  height: 100vh;
  height: 100dvh;
  margin: 0 auto;
  background: #fff;
  font-family: 'PingFang SC', 'Microsoft YaHei', Avenir, Helvetica, Arial, sans-serif;
  color: #333;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ① 顶部固定区 */
.record-top {
  flex-shrink: 0;
  z-index: 30;
  background: #fff;
}

.game-pick {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 44px;
  padding: 0;
  border: 0;
  background: #fff;
  color: #333;
  font-size: 15px;
  cursor: pointer;
}

.caret {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #999;
}

.date-tabs {
  display: flex;
  margin: 0 12px 10px;
  border: 1px solid #d8d8d8;
  overflow: hidden;
}

.date-tab {
  flex: 1;
  height: 32px;
  padding: 0;
  border: 0;
  border-right: 1px solid #d8d8d8;
  background: #fff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
}

.date-tab:last-child {
  border-right: 0;
}

.date-tab.on {
  background: #f42c4c;
  color: #fff;
}

/* ② 中间滚动区：隐藏滚动条（Chrome / Edge / Firefox / IE） */
.record-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE / 旧 Edge */
}

.record-scroll::-webkit-scrollbar {
  display: none; /* Chrome / Edge / Safari */
  width: 0;
  height: 0;
}

.loading-tip {
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
  padding: 40px 0;
  box-sizing: border-box;
}

.empty-state {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 12vh 24px 24px;
  box-sizing: border-box;
  background: #fff;
}

.empty-state__icon {
  width: 168px;
  height: auto;
  max-height: 160px;
  object-fit: contain;
  display: block;
}

.empty-state__title {
  margin: 20px 0 8px;
  color: #595959;
  font-size: 15px;
  line-height: 1.4;
  font-weight: 500;
}

.empty-state__hint {
  margin: 0;
  color: #8f8e8f;
  font-size: 13px;
  line-height: 1.5;
}

.empty-state__link {
  padding: 0;
  border: 0;
  background: none;
  color: #8f8e8f;
  font-size: 13px;
  line-height: inherit;
  cursor: pointer;
}

.record-list {
  list-style: none;
  margin: 0;
  padding: 0 12px;
  background: transparent;
}

.record-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 0;
  border-bottom: 1px solid #e8e8e8;
}

.record-item:last-child {
  border-bottom: 0;
}

.record-logo {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  object-fit: contain;
  margin-top: 2px;
}

.record-body {
  flex: 1;
  min-width: 0;
}

.record-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.record-game {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  letter-spacing: 0.5px;
}

.record-status {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 12px;
  background: #389ff3;
  color: #fff;
  font-size: 11px;
  line-height: 1.4;
}

.record-status.win {
  background: #ea0f00;
}

.record-status.lose,
.record-status.cancel {
  background: #bdbdbd;
}

.record-play {
  font-size: 13px;
  color: #ff6203;
  margin-bottom: 2px;
}

.record-content {
  font-size: 13px;
  color: #666;
  line-height: 1.45;
  word-break: break-all;
  margin-bottom: 6px;
}

.record-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
}

.record-numero {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-time {
  flex-shrink: 0;
}

.record-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
}

.record-amount {
  color: #333;
  font-weight: 500;
}

.record-win {
  color: #ea0f00;
  font-weight: 600;
}

/* ③ 底部固定：金额合计（覆盖全局 .sum-row 白底/折线） */
.sum-foot {
  flex-shrink: 0;
  z-index: 30;
  box-sizing: border-box;
  width: 100%;
  padding: 12px 16px 14px;
  background: #f5f5f5;
  border: 0;
}

.page-wrap .sum-foot .sum-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 0;
  padding: 5px 0;
  color: #333;
  font-size: 13px;
  line-height: 1.4;
  background: transparent;
  border: 0;
  border-bottom: 0;
  box-shadow: none;
}

.sum-label {
  color: #333;
  flex-shrink: 0;
}

.sum-value {
  color: #333;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.mask {
  position: fixed;
  inset: 0;
  z-index: 40;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.lot-panel {
  width: 100%;
  max-width: 480px;
  max-height: 72vh;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.lot-title {
  flex-shrink: 0;
  height: 44px;
  line-height: 44px;
  text-align: center;
  background: #ececec;
  color: #555;
  font-size: 15px;
}

.lot-scroll {
  overflow-y: auto;
  max-height: calc(72vh - 92px);
  background: #fff;
}

.lot-game {
  display: block;
  width: 100%;
  height: 44px;
  padding: 0;
  border: 0;
  border-bottom: 1px solid #f2f2f2;
  background: #fff;
  color: #333;
  font-size: 15px;
  cursor: pointer;
}

.lot-game.on {
  color: #f42c4c;
}

.lot-cancel {
  flex-shrink: 0;
  height: 48px;
  border: 0;
  background: #f42c4c;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
}

/* ========== 深色主题（.night / theme-dark 同步） ========== */
.night {
  background: #222;
  color: #fff;
}

.night .record-top {
  background: #222;
}

.night .game-pick {
  background: #222;
  color: #fff;
}

.night .caret {
  border-top-color: #ccc;
}

.night .date-tabs {
  border-color: #555;
}

.night .date-tab {
  background: #2a2a2a;
  color: #fff;
  border-color: #555;
}

.night .date-tab.on {
  background: #f42c4c;
  color: #fff;
}

.night .record-scroll {
  background: #222;
}

.night .loading-tip {
  color: #999;
}

.night .empty-state {
  background: #222;
}

.night .empty-state__title {
  color: #888;
}

.night .empty-state__hint,
.night .empty-state__link {
  color: #999;
}

.night .record-item {
  border-color: #3a3a3a;
}

.night .record-game,
.night .record-amount {
  color: #eee;
}

.night .record-content,
.night .record-meta,
.night .record-numero,
.night .record-time {
  color: #999;
}

.night .record-status.lose,
.night .record-status.cancel {
  background: #666;
  color: #fff;
}

.night .sum-foot {
  background: #2c2e35;
  border: 0;
}

.page-wrap.night .sum-foot .sum-row,
.night .sum-label,
.night .sum-value {
  color: #fff;
  background: transparent;
  border: 0;
  border-bottom: 0;
}

.night .lot-panel,
.night .lot-scroll,
.night .lot-game {
  background: #2c2e35;
  color: #ddd;
}

.night .lot-title {
  background: #3a3d45;
  color: #ccc;
}

.night .lot-game {
  border-color: #3f424a;
}
</style>
